<template>
  <SectionContainer :colors="colors" :background-variant="backgroundVariant" >
    <div class="w-full flex flex-col md:flex-row items-center justify-center">
      <div
          v-motion
          :initial="{ opacity: 0, x: textLeft ? -200 : 100 }"
          :visible="{ opacity: 1, x: 0 }"
          :transition="{ duration: 2, delay: 2, ease: 'easeOut' }"
          class="w-full p-10 flex items-center justify-center h-full order-3 border-transparent"
          :class="[textLeft ? 'md:order-1' : 'md:order-3',  (src && src.url !== '') || showPhotoSlot ? 'md:w-1/2' : '']"
          :style="cssVars"
      >
        <div>
          <button v-if="link" class="flex flex-row items-center cursor-pointer link-style" @click="navigate">
            <component :is="headingTag" class="text-3xl font-bold link-text">
              {{ title }}
            </component>
            <ArrowRight class="ml-1 h-4 arrow-icon"/>
          </button>
          <div v-else class="flex flex-row items-center heading-style">
            <component :is="headingTag" class="text-3xl font-bold ">
              {{ title }}
            </component>
          </div>

          <p class="mt-4 text-lg" style="color: var(--theme-text-body);">
            {{ content }}
          </p>

          <slot></slot>

          <WebButton v-if="callToAction" @clicked="callToActionClicked">
            {{ callToAction }}
          </WebButton>
        </div>
      </div>

      <div class="hidden md:block w-8"></div>

      <!-- Image Side -->
      <div  v-if="src && src.url !== ''"
            v-motion
            :initial="{ opacity: 0, scale: 0.9 }"
            :visible="{ opacity: 1, scale: 1 }"
            :transition="{ duration: 2, delay: 1.8 }"
            class="w-full md:w-1/2 h-full relative order-1 "
            :class="textLeft ? 'md:order-3' : 'md:order-1'"
      >
        <div v-if="imageLoaded" class="h-full w-full rounded-md">
          <img class="w-full h-full max-h-[80dvh] object-contain" :class="imgClass" :src="src.url" v-bind="imgAttrs(src.url, '(min-width: 768px) 50vw, 100vw')" :alt="imgAlt(src.url, title, alt)" @error="imageLoaded = false"/>
        </div>
      </div>
      <!-- Website Studio only: the empty photo column, marked. -->
      <div v-else-if="showPhotoSlot"
           class="w-full md:w-1/2 h-full relative order-1 px-6 md:px-0"
           :class="textLeft ? 'md:order-3' : 'md:order-1'"
      >
        <StudioPhotoSlot />
      </div>
    </div>
  </SectionContainer>
</template>

<script setup>
import {useSectionHeadingTag} from "@/composables/useSectionHeadingTag.js";
import {useImageAlt} from "@/composables/useImageAlt.js";
const imgAlt = useImageAlt();
import {imgAttrs} from "@/utils/photoSizes.js";
import WebButton from "@/components/WebButton.vue";
import {ArrowRight} from "@iconoir/vue";
import router from "@/router/index.js";
import SectionContainer from "@/components/SectionContainer.vue";
import {computed, ref} from "vue";
import {storeToRefs} from "pinia";
import {useColorStore} from "@/classes/useColorStore.js";
import {resolveMediaUrl} from "@/composables/images.js";
import StudioPhotoSlot from "@/components/StudioPhotoSlot.vue";
import {studioMode} from "@/classes/previewBridge.js";
import {photoSlotEmpty} from "@/utils/studioLogic.js";
import {useBackLinks} from "@/composables/useBackLinks.js";
const back = useBackLinks();
const headingTag = useSectionHeadingTag();

const {theme} = storeToRefs(useColorStore());
const imageLoaded = ref(true);
const imgClass = computed(() => {
  switch (theme.value) {
    case "flat":
      return "px-2";
    case "glass":
      return "rounded-xl";
    case "brutalist":
      return "border-4 border-black";
    case "neon":
      return "rounded-md border border-[var(--brand-accent)] shadow-[0_0_16px_var(--brand-accent)]";
    case "corporate":
      return "rounded-sm border border-neutral-200";
    default:
      return "";
  }
})

const props = defineProps({
  title: {type: String, required: true},
  content: {type: String, required: true},
  src: {type: String, required: true},
  alt: {type: String, default: "section image"},
  textLeft: {type: Boolean, default: true},
  callToAction: {type: String, default: ""},
  link: {type: String, default: ""},
  colors: {type: Object, required: true},
  backgroundVariant: {type: Number, default: 1}
});

// Studio mode only (always 'off' in SSR and live): keep the photo column so
// "Your photo goes here" can sit in it.
const showPhotoSlot = computed(() => studioMode.value !== 'off' && photoSlotEmpty({type: 'split', image: props.src}));

const callToActionClicked = () => {

}


// Navigate using Vue Router
const navigate = () => {
  if (props.link) {
    router.push(back(props.link));
  }
};

const cssVars = computed(() => ({
  "--bg-light": !props.forceBlandColors && props.backgroundVariant === 2 ? "var(--brand-lightest)" : "var(--brand-whitest)",
  "--bg-dark": "var(--brand-darkest)",
  "--color-accent": "var(--brand-accent)"
}));
</script>

<style scoped>
/* Link Styles */
.link-style {
  color: var(--link-color);
  transition: color 0.2s ease-in-out;
}

.link-style:hover {
  color: var(--link-hover-color);
}

.heading-style {
  color: var(--color-accent);
}

/* Arrow Hover Animation */
.arrow-icon {
  transition: transform 0.2s ease-in-out;
}

.link-style:hover .arrow-icon {
  transform: translateX(3px);
}



</style>
